import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NoticeBody from '../../components/NoticeBody';
import PrivacyNoticeConsent from '../../components/PrivacyNoticeConsent';
import TermsConsent from '../../components/TermsConsent';
import { usePrivacyNotice } from '../../hooks/usePrivacyNotice';
import { useTerms } from '../../hooks/useTerms';
import { getApiErrorMessage } from '../../lib/apiError';
import { movedDocument, owedDocuments } from '../../lib/legalDocuments';
import legalDocumentsService from '../../services/legalDocuments.service';
import { dashboardPathForRole, type Role } from '../../lib/roleRouting';
import { useAuthStore } from '../../stores/authStore';
import { colors } from '../../styles/theme';

const TEXT_BOX_CLASS = 'mt-2 max-h-72 overflow-y-auto rounded-xl p-4';

/**
 * The one screen a client reaches after signing in while they owe the Terms and Conditions and/or
 * the privacy notice in force (owner, 2026-10-28): the account is created without accepting
 * either, so the first sign-in lands here, and a new version of either brings the person back.
 * Until they accept, every other route bounces here. Each document the account owes has its own
 * box, ticked by the person: "I accept the Terms and Conditions" and the express consent to the
 * processing of financial and patrimonial data under the Privacy Notice, whose text is shown
 * here. Accepting records each acceptance on its own row, in one request, and sends the person to
 * their dashboard. A document that is not owed is not shown.
 */
export default function AcceptLegalDocuments(): JSX.Element {
  const navigate = useNavigate();
  const privacy = usePrivacyNotice();
  const termsState = useTerms();
  const { user } = useAuthStore();
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const owed = owedDocuments(user);
  const privacyOwed = owed.includes('privacy');
  const termsOwed = owed.includes('terms');

  // Nothing owed (settled, or the page was opened by hand): nothing to do here.
  useEffect(() => {
    if (owed.length === 0) {
      navigate(dashboardPathForRole((user?.role ?? null) as Role | null), { replace: true });
    }
  }, [owed.length, navigate, user?.role]);

  const clearOwed = (): void => {
    const { user: current, setUser } = useAuthStore.getState();
    if (current) setUser({ ...current, privacy_notice_owed: false, terms_owed: false });
  };

  const accept = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await legalDocumentsService.accept({
        terms_version: termsOwed ? termsState.terms?.version : undefined,
        privacy_notice_version: privacyOwed ? privacy.notice?.version : undefined,
      });
      clearOwed();
    } catch (e) {
      // The version in force moved while the page was open: show the new text and ask again.
      const moved = movedDocument(e);
      if (moved === 'privacy') privacy.reload();
      if (moved === 'terms') termsState.reload();
      if (moved) {
        setPrivacyChecked(false);
        setTermsChecked(false);
      }
      setError(
        moved
          ? 'The text changed while you were reading it. Please read the new version and accept it again.'
          : getApiErrorMessage(e, 'Could not record your acceptance.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const ready =
    (!privacyOwed || (privacyChecked && privacy.notice !== null)) &&
    (!termsOwed || (termsChecked && termsState.terms !== null));
  const disabled = busy || !ready || owed.length === 0;

  return (
    <div className="min-h-screen p-6" style={{ background: colors.canvas }}>
      <main
        className="mx-auto max-w-2xl rounded-2xl p-8"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <h1 className="text-2xl font-bold mb-2" style={{ color: colors.ink }}>
          Terms and Conditions
        </h1>
        <p className="mb-4 text-sm" style={{ color: colors.muted }}>
          {owed.length > 1
            ? 'Please read the current Terms and Conditions and the Privacy Notice and accept both to continue.'
            : 'Please read the current text and accept it to continue.'}
        </p>
        {error && (
          <p role="alert" className="mb-3 text-sm" style={{ color: colors.danger }}>
            {error}
          </p>
        )}
        {privacyOwed && (
          <section className="mb-5">
            <h2 className="text-base font-semibold" style={{ color: colors.ink }}>
              Privacy notice
            </h2>
            <div className={TEXT_BOX_CLASS} style={{ border: `1px solid ${colors.border}` }}>
              <NoticeBody notice={privacy.notice} unavailable={privacy.unavailable} />
            </div>
          </section>
        )}
        <div className="mb-5 space-y-3">
          {termsOwed && (
            <TermsConsent
              state={termsState}
              checked={termsChecked}
              onChange={setTermsChecked}
              disabled={busy}
            />
          )}
          {privacyOwed && (
            <PrivacyNoticeConsent
              state={privacy}
              checked={privacyChecked}
              onChange={setPrivacyChecked}
              disabled={busy}
              express
            />
          )}
        </div>
        <button
          type="button"
          onClick={() => void accept()}
          disabled={disabled}
          className="rounded-xl px-5 py-2.5 text-sm font-semibold"
          style={{
            background: disabled ? colors.disabledBg : colors.gold,
            color: disabled ? colors.disabledText : colors.navy,
          }}
        >
          {busy ? 'Saving…' : 'Accept and continue'}
        </button>
      </main>
    </div>
  );
}
